import { Link } from "react-router-dom";
import { SquareDealLink } from "@/components/SquareDealLink";
import { LEGAL_CONTACT_EMAIL } from "@/utils/legalDocs";

const p = "mb-3 last:mb-0";

const ANSWERS = {
  "what-is-terrakronos": (
    <>
      <p className={p}>
        TerraKronos is transaction management for Hawaii real estate agents. It counts weekends
        and Hawaii holidays on your contract dates, reminds you before something is due, and lets
        your client open the same dates from a link.
      </p>
      <p className={p}>
        Mainland tools treat Kamehameha Day like a normal Monday. A spreadsheet does not watch
        dates that wait on each other. This app does both, so a missed clock is less likely to
        cost you the file.
      </p>
    </>
  ),
  "no-google": (
    <>
      <p className={p}>
        You do not need Google. On the sign-in page, choose{" "}
        <strong>Email me a sign-in link</strong>. We send a one-time link to that address. There
        is no password to set or remember.
      </p>
      <p className={p}>
        The same email can use Google later. It is still one account. We do not overwrite your
        name or photo if you already signed in the other way.
      </p>
    </>
  ),
  "credit-card": (
    <>
      <p className={p}>
        No. Open a free account and keep it as long as you want. Pay only when you need more
        deals, papers on the file, or a Team.
      </p>
      <p className={p}>
        Current prices and deal limits are on the{" "}
        <Link to="/#pricing" className="text-primary hover:underline">
          home page under Pricing
        </Link>
        .
      </p>
    </>
  ),
  "try-without-account": (
    <>
      <p className={p}>
        Yes. The{" "}
        <Link to="/tools" className="text-primary hover:underline">
          Hawaii calculators
        </Link>{" "}
        work with no login: conveyance tax, HARPTA, FIRPTA, property tax, and closing estimates.
      </p>
      <p className={p}>
        Sign up when you want holiday-aware deal dates, reminders, and a client link. A free
        account is enough to open a real file and see if the dates stay straight.
      </p>
    </>
  ),
  "client-link": (
    <>
      <p className={p}>
        Yes. You send a link. They see the dates you chose to share. They do not get the rest of
        the file, and they do not need a TerraKronos login.
      </p>
    </>
  ),
  "plan-limits": (
    <>
      <p className={p}>
        <strong>Archived deals still count.</strong> Archive hides a file from your everyday list,
        but it still uses a slot on your plan. That way an old closing does not disappear, and it
        does not pretend to free a seat.
      </p>
      <p className={p}>
        <strong>Deleted deals do not count.</strong> A deleted file sits in a deleted list for
        about 30 days, then it is removed. If you are at your limit, delete a file you no longer
        need, or move up a plan.
      </p>
      <p className={p}>
        Live deal limits are on the{" "}
        <Link to="/#pricing" className="text-primary hover:underline">
          Pricing
        </Link>{" "}
        section of the home page. They can change when plans change, so we do not freeze a number
        here.
      </p>
    </>
  ),
  "whats-free": (
    <>
      <p className={p}>
        Holiday-aware due dates, reminders, a share link for your client, and the Hawaii tax
        calculators. The free plan is there so you can run a real file, not a demo that hides the
        dates.
      </p>
      <p className={p}>
        Paid plans add more deals, documents on the file, a Team, and a transaction coordinator
        role. See{" "}
        <Link to="/#pricing" className="text-primary hover:underline">
          Pricing
        </Link>{" "}
        for what each plan includes today.
      </p>
    </>
  ),
  cancel: (
    <>
      <p className={p}>
        Yes. Cancel from Settings, then Subscription. You keep access through the period you
        already paid. There is no long contract.
      </p>
    </>
  ),
  security: (
    <>
      <p className={p}>
        We protect data in transit and at rest with industry-standard measures. We do not sell
        your personal information. We do not use advertising cookies or third-party analytics
        pixels.
      </p>
      <p className={p}>
        You sign in with Google or a one-time email link, so there is no TerraKronos password
        sitting in our database for a new account.
      </p>
      <p className={p}>
        You can turn on extra encryption for files you upload. Those files are locked on your
        device before we store them. We do not have your passphrase or recovery phrase, and we
        cannot read those locked files. If you lose both, we cannot unlock them for you.
      </p>
      <p className={p}>
        Encryption is not on every deal field. Names, addresses, dates, notes, and similar text
        stay readable by the service so the app can show them to you and to people you authorize.
        The{" "}
        <Link to="/privacy" className="text-primary hover:underline">
          Privacy Policy
        </Link>{" "}
        is the full picture.
      </p>
    </>
  ),
  "who-can-see": (
    <>
      <p className={p}>
        You, plus people you invite or share with. A Team people list is visible to that Team. A
        share link works like a secret: anyone who has the link can see what you allowed until
        you change or remove it.
      </p>
    </>
  ),
  squaredeal: (
    <>
      <p className={p}>
        <SquareDealLink /> is an independent Hawaii purchase-contract form. TerraKronos can fill
        the current SquareDeal pack and make a PDF. You still check that PDF against what your
        clients agreed to. The printed form belongs to SquareDeal, not to us.
      </p>
      <p className={p}>
        TerraKronos is not SquareDeal, and SquareDeal is not a Hawaii Association of Realtors
        form. If you work on SquareDeal paper, you can type the answers once in the app instead
        of hunting fields on every revision.
      </p>
    </>
  ),
  "har-forms": (
    <>
      <p className={p}>
        Not as a filled HAR PDF. A HAR fill needs that publisher&apos;s written permission, and
        we do not promise it.
      </p>
      <p className={p}>
        You can still run an HAR file in TerraKronos for dates, people, documents, and a client
        link. The clocks are the point. The paper stays the paper.
      </p>
    </>
  ),
  "calculator-accuracy": (
    <>
      <p className={p}>
        The calculators read stored official tables, not numbers typed into the screen. Property
        tax uses each county&apos;s adopted rate-setting resolution. HARPTA uses Hawaii Revised
        Statutes § 235-68. FIRPTA uses 26 U.S.C. § 1445. Conveyance tax uses HRS § 247-2. VA
        funding fees use va.gov.
      </p>
      <p className={p}>
        We check those sources on a schedule. If a check fails, we keep the last good table
        rather than guess. We do not scrape brochures or third-party reprints for the numbers
        the calculator uses.
      </p>
      <p className={p}>
        The results are estimates. They are not legal, tax, or financial advice, and they can
        differ from the final closing statement. Confirm amounts against the official paper and
        your deal. A licensed professional should review anything you rely on.
      </p>
      <p className={p}>
        Try them now, no account:{" "}
        <Link to="/tools" className="text-primary hover:underline">
          Hawaii Calculators
        </Link>
        .
      </p>
    </>
  ),
  "not-a-crm": (
    <>
      <p className={p}>
        No. TerraKronos is not a CRM and not an MLS. It is the Hawaii file: dates, people on
        that deal, papers, and the tax math you need at the table.
      </p>
      <p className={p}>
        Keep your CRM if you like it. Use this when a holiday, a relative clock, or a client
        asking &quot;when is this due?&quot; is the thing that usually goes wrong.
      </p>
    </>
  ),
};

export function FaqAnswer({ id }) {
  return ANSWERS[id] || null;
}

export function FaqContactLine() {
  return (
    <>
      Still deciding? Email{" "}
      <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} className="text-primary hover:underline">
        {LEGAL_CONTACT_EMAIL}
      </a>
      , or open a free account and try one deal.
    </>
  );
}
